import iFetch from 'isomorphic-fetch';
import * as R from 'ramda';

import { API_CREDENTIALS, API_REQUEST_TIMEOUT_MS } from '../../../constants';

import { AppError } from './app-error';
import { createTimeoutError } from './handle-error';

const getConfig = (method) => ({
    credentials: API_CREDENTIALS,
    method,
    headers: {
        Accept: 'application/json',
        Cookie: document.cookie,
        'Content-Type': 'application/json',
    },
});

if (!globalThis.fetch) {
    globalThis.fetch = iFetch;
}

const handleResponse = (response) => {
    if (!response.ok) {
        throw AppError.from(response);
    }
    return response.json();
};

const getAbortController = () =>
    typeof globalThis.AbortController === 'function' ? new globalThis.AbortController() : null;

const getAbortReason = (signal) =>
    // eslint-disable-next-line no-undefined
    signal?.reason === undefined ? new Error('Request aborted') : signal.reason;

const NOOP = () => {};
const neverSettlingPromise = () => new Promise(NOOP);
const emptyJsonBody = () => ({});

const buildSsoRedirectUrl = (data) =>
    data?.redirect_url
        ? `${data.redirect_url}?url=${globalThis.location.href}`
        : 'https://www.sans.org/';

// Curried 401 continuation: parses the redirect body, then once the body is in
// hand checks that settle wins the race (otherwise a timeout/abort already
// rejected the outer promise and we MUST NOT trigger navigation). When settle
// wins, fire the SSO redirect and return a never-settling promise so caller
// .then chains can't run against an empty body during navigation.
const navigateOrBail = (settle) => (data) => {
    if (!settle(NOOP)) {
        return neverSettlingPromise();
    }
    globalThis.location.assign(buildSsoRedirectUrl(data));
    return neverSettlingPromise();
};

const handle401Redirect = (response, settle) =>
    response.json().catch(emptyJsonBody).then(navigateOrBail(settle));

const routeResponse = (settle) => (response) => {
    if (response.status === 401) {
        return handle401Redirect(response, settle);
    }
    return handleResponse(response);
};

const onFetchSettled = (settle, resolve, reject, url) => [
    (value) => settle(() => resolve(value)),
    (cause) => {
        if (cause instanceof AppError) {
            settle(() => reject(cause));
            return;
        }
        settle(() => reject(AppError.from(cause, { url })));
    },
];

// Wraps fetch with a default request timeout (overridable per call) and an
// optional caller-supplied AbortSignal. Resolves with parsed JSON on 2xx;
// rejects with an AppError on non-2xx, network failure, or timeout; rejects
// with the caller's signal reason (verbatim) when a caller signal aborts.
const requestWithTimeout = (url, config, options = {}) =>
    new Promise((resolve, reject) => {
        const timeoutMs = typeof options.timeout === 'number' ? options.timeout : API_REQUEST_TIMEOUT_MS;
        const callerSignal = options.signal;

        if (callerSignal?.aborted) {
            reject(getAbortReason(callerSignal));
            return;
        }

        const abortController = getAbortController();
        const fetchConfig = abortController ? R.assoc('signal', abortController.signal, config) : config;
        const timer = { id: null };
        let settled = false;
        let callerAbortListener = null;

        const settle = (action) => {
            if (settled) {
                return false;
            }
            settled = true;
            clearTimeout(timer.id);
            if (callerSignal && callerAbortListener) {
                callerSignal.removeEventListener('abort', callerAbortListener);
            }
            action();
            return true;
        };

        timer.id = setTimeout(() => {
            settle(() => {
                if (abortController) {
                    abortController.abort();
                }
                reject(createTimeoutError(timeoutMs, url));
            });
        }, timeoutMs);

        if (callerSignal) {
            callerAbortListener = () => {
                const reason = getAbortReason(callerSignal);
                settle(() => {
                    if (abortController) {
                        abortController.abort();
                    }
                    reject(reason);
                });
            };
            callerSignal.addEventListener('abort', callerAbortListener);
        }

        // 401 triggers an SSO redirect and the wrapper intentionally never
        // settles so caller .then chains can't run against an empty body
        // while the page is navigating. The redirect body parse stays under
        // the timeout window; once the parse resolves and we're committed to
        // navigating, the continuation clears the timer (or bails if the
        // timer already won the race). See navigateOrBail.
        const [onSettled, onFailure] = onFetchSettled(settle, resolve, reject, url);
        fetch(url, fetchConfig)
            .then(routeResponse(settle))
            .then(onSettled, onFailure);
    });

const getFetch = (url, options) => requestWithTimeout(url, getConfig('GET'), options);

export const handledGetFetch = (url, options) => getFetch(url, options);

const postFetch = (url, body, options) =>
    requestWithTimeout(url, R.assoc('body', JSON.stringify(body), getConfig('POST')), options);

export const handledPostFetch = (url, body, options) => postFetch(url, body, options);

const putFetch = (url, body, options) =>
    requestWithTimeout(url, R.assoc('body', JSON.stringify(body), getConfig('PUT')), options);

export const handledPutFetch = (url, body, options) => putFetch(url, body, options);

const deleteFetch = (url, options) => requestWithTimeout(url, getConfig('DELETE'), options);

export const handledDeleteFetch = (url, options) => deleteFetch(url, options);
